<template>
  <div class="dialog-add-account" v-loading="loading">
    <div class="dialog-add-account__title" v-html="$t('business_account_data.title')"></div>
    <div class="dialog-add-account__desc">
      {{ $t('business_account_data.description') }}
    </div>

    <form class="dialog-add-account__form">
      <div class="dialog-add-account__form-row is-options-row">
        <div
          class="dialog-add-account__form-col dialog-add-account__form-col--big with-options"
          :class="{ 'with-options': isEditable }"
        >
          <AccountTypeSwitcher v-model="form.is_legal" :show="showSwitcher" />

          <div
            v-if="role === 'manager' || role === 'finance' || role === 'admin'"
            class="dialog-add-account__show-settings"
            :class="{ 'dialog-add-account__show-settings--show': showSettings }"
            @click="showSettings = !showSettings"
          >
            <img src="@/assets/icons/icon-settings.svg" alt="settings" title="settings" />
            <span>{{ $t('business_account_data.settings') }}</span>
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5625 4.46875L6.5 8.53125L2.4375 4.46875"
                stroke="#333333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>

      <transition name="fade">
        <BusinessAccountSettings
          :show="showSettings"
          :role="role"
          :managers="managers"
          :manager-id="form.manager_id"
          :id-prefix="form.ba_prefix"
          :agency-account-type="agency_account_type"
          :is-editable="isEditable"
          :course-percents="form.course_percents"
          :has-active-account="!!activeAccount"
          :advertising-tax="form.advt_tax"
          :tax-conditions="form.tax_conditions"
          :visible-services="form.visible_services"
          :automation-data="automationData"
          @manager-change="getNewData"
          @update:id-prefix="form.ba_prefix = $event"
          @update:agency-account-type="handleAgencyAccountTypeUpdate"
          @update:course-percents="form.course_percents = $event"
          @update:advertising-tax="form.advt_tax = $event"
          @update:tax-conditions="form.tax_conditions = $event"
          @update:visible-services="form.visible_services = $event"
          @update:auto-acts="handleAutomationUpdate('auto_acts', $event)"
          @update:auto-cashback="handleAutomationUpdate('auto_cashback', $event)"
          @update:access-api="handleAutomationUpdate('access_api', $event)"
          @update:is-force-bonus="handleAutomationUpdate('is_force_bonus', $event)"
        />
      </transition>

      <div class="dialog-add-account__form-title">
        {{ $t('business_account_data.company') }}
      </div>

      <div class="dialog-add-account__form-block">
        <div class="dialog-add-account__form-row">
          <InnInput
            :value="form.inn"
            :error="errors.inn"
            :server-errors="authErrors"
            :disabled="isEditable && role === 'user'"
            :is-validated="isCorrectInn"
            :loading="loading"
            :show-fio-field="false"
            :check-action="'Ad/checkInn'"
            :allow-recheck-when-validated="locale === 'RU'"
            @input="form.inn = $event"
            @check="handleCheckInn"
            @error="errors.inn = $event"
            @validation-reset="isCorrectInn = false"
          />

          <template v-if="isCorrectInn">
            <CompanyInfoForm
              :value="companyInfoData"
              :errors="companyInfoErrors"
              :server-errors="authErrors"
              :disabled="false"
              :is-legal-address-disabled="isDisabledLegalAddress"
              :is-post-address-disabled="inputAddressDisabled"
              @update:legal_name="handleCompanyInfoUpdate('legal_name', $event)"
              @update:legal_address="handleCompanyInfoUpdate('legal_address', $event)"
              @update:legal_receiving_physical_address="
                handleCompanyInfoUpdate('legal_receiving_physical_address', $event)
              "
              @update:toggleAddress="handleCompanyInfoUpdate('toggleAddress', $event)"
              @toggle-address-change="handleToggleAddressChange"
            />
          </template>
        </div>
      </div>

      <template v-if="isCorrectInn">
        <!-- The contact person -->
        <ContactPersonForm
          :value="contactPersonData"
          :errors="contactPersonErrors"
          :server-errors="authErrors"
          @update:name="handleContactPersonUpdate('name', $event)"
          @update:phone="handleContactPersonUpdate('phone', $event)"
          @update:email="handleContactPersonUpdate('email', $event)"
        />

        <DocumentReceivingSettings
          :value="documentReceivingData"
          :errors="documentReceivingErrors"
          :server-errors="authErrors"
          @input="handleDocumentReceivingUpdate"
        />

        <BankDetailsForm
          :bank-data="bankFormData"
          :errors="bankErrors"
          :server-errors="authErrors"
          :locale="locale"
          @update:bankData="handleBankDataUpdate"
        />

        <AgreementCheckboxes
          :value="agreementData"
          :error="errors.isAgree"
          :show="showSwitcher"
          @update:offer_agreement_accepted="
            handleAgreementUpdate('offer_agreement_accepted', $event)
          "
          @update:isAgreePolicy="handleAgreementUpdate('isAgreePolicy', $event)"
        />
        <div class="dialog-add-account__form-buttons">
          <ButtonElement :text="$t('button_props.save')" @click-button="saveData" />
          <ButtonElement
            :text="$t('button_props.cancel')"
            view="empty"
            @click-button="closeDialog"
          />
        </div>
      </template>
    </form>
  </div>
</template>

<script>
import formValidate from '../../mixin/formValidate';
import ButtonElement from '../../components/elements/ButtonElement';
import AccountTypeSwitcher from '../forms/businessAccount/AccountTypeSwitcher.vue';
import BankDetailsForm from '../forms/BankDetailsForm.vue';
import InnInput from '../forms/InnInput.vue';
import BusinessAccountSettings from '../forms/businessAccount/BusinessAccountSettings.vue';
import DocumentReceivingSettings from '../forms/businessAccount/DocumentReceivingSettings.vue';
import ContactPersonForm from '../forms/businessAccount/ContactPersonForm.vue';
import CompanyInfoForm from '../forms/businessAccount/CompanyInfoForm.vue';
import AgreementCheckboxes from '../forms/businessAccount/AgreementCheckboxes.vue';

export default {
  name: 'BusinessAccountData',
  components: {
    ButtonElement,
    AccountTypeSwitcher,
    BankDetailsForm,
    InnInput,
    BusinessAccountSettings,
    DocumentReceivingSettings,
    ContactPersonForm,
    CompanyInfoForm,
    AgreementCheckboxes,
  },
  mixins: [formValidate],
  data() {
    return {
      locale: process.env.VUE_APP_LOCALE,
      loading: false,
      isCorrectInn: false,
      inputAddressDisabled: false,
      isDisabledLegalAddress: false,
      form: {
        partner: '',
        is_legal: true,
        email: '',
        phone: '',
        inn: '',
        bic: '',
        iban: '',
        swift_code: '',
        bank_name: '',
        bank_address: '',
        manager_id: '',
        legal_name: '',
        legal_address: '',
        legal_country: 'Russia',
        legal_contact_name: '',
        legal_receiving_docs_method: 'not_receive',
        legal_receiving_mail_index: '',
        legal_receiving_mail_address: '',
        legal_receiving_mail_recipient: '',
        position: '',
        based_position: '',
        toggleAddress: '',
        legal_receiving_physical_address: '',
        course_percents: { EUR: 5, USD: 5, PLN: 5 },
        tax_conditions: {
          mytarget: false,
          vkontakte: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          facebook: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          yandex: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          vkads: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          telegram: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          tiktok: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          google: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          promopages: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          avito: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          vkmarket: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          metricapro: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          mediaplatform: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          yandexbusiness: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
          vkadblogger: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          ozon: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          urbanads: process.env.VUE_APP_LOCALE === 'RU' ? false : false,
        },
        visible_services: {
          mytarget: false,
          vkontakte: false,
          facebook: process.env.VUE_APP_LOCALE === 'RU' ? false : true,
          yandex: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          google: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          vkads: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          telegram: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          tiktok: process.env.VUE_APP_LOCALE === 'RU' ? false : true,
          promopages: process.env.VUE_APP_LOCALE === 'RU' ? true : false,
          avito: false,
          vkmarket: false,
          metricapro: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          mediaplatform: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          yandexbusiness: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          vkadblogger: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          ozon: process.env.VUE_APP_LOCALE === 'RU' ? true : true,
          urbanads: false,
        },
        offer_agreement_accepted: false,
        isAgree: '',
        isAgreePolicy: false,
        tracker_manager_id: '',
        course_percent: 0,
        ba_prefix: '',
        advt_tax: 3, // Рекламный сбор в процентах
        first_name: '',
        last_name: '',
        contract_signature_firstname: '',
        contract_signature_lastname: '',
        contract_signature_position: '',
        contract_signature_document: '',
        auto_acts: true,
        auto_cashback: true,
        access_api: false,
        is_force_bonus: true, // По умолчанию включен (сохраняем текущую логику)
      },
      errors: {
        is_legal: false,
        legal_name: '',
        inn: '',
        legal_address: '',
        legal_contact_name: '',
        bank_name: '',
        email: '',
        phone: '',
        agree_terms_contract: '',
        legal_receiving_mail_index: '',
        legal_receiving_mail_recipient: '',
        legal_receiving_mail_address: '',
        legal_receiving_physical_address: '',
        bic: '',
        swift_code: '',
        bank_address: '',
        legal_receiving_docs_method: '',
        offer_agreement_accepted: '',
        isAgreePolicy: '',
        isAgree: '',
        position: '',
        based_position: '',
        contract_signature_firstname: '',
        contract_signature_lastname: '',
        contract_signature_position: '',
        contract_signature_document: '',
        bank_error: '',
      },
      showSettings: false,
      agency_account_type: {
        mytarget: 'profitads',
        yandex: 'profitads',
        vkontakte: 'profitads',
        vkads: 'profitads',
        promopages: 'default',
      },
    };
  },
  computed: {
    showSwitcher() {
      if (this.$route && this.$route.name !== 'ClientsInfo' && this.isOpenUL) {
        return false;
      } else if (this.$route && this.$route.name === 'ClientsInfo') {
        return true;
      } else if (
        this.$route &&
        this.$route.name !== 'ClientsInfo' &&
        (this.user.role !== 'admin' || this.user.role !== 'finance')
      ) {
        return true;
      } else {
        return false;
      }
    },
    isOpenUL() {
      return this.$store.state.Dialog.isOpenUL;
    },
    accountAutocomplete() {
      if (this.$route && this.$route.name === 'ClientsInfo') {
        return false;
      }
      return true;
    },
    isEditable() {
      return this.$store.state.Dialog.isEditable;
    },
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    user() {
      return this.$store.state.Auth.user;
    },
    role() {
      return this.$store.state.Auth.role;
    },
    isLegal() {
      return this.form.is_legal;
    },
    documentReceivingData() {
      return {
        method: this.form.legal_receiving_docs_method || 'not_receive',
        mail_index: this.form.legal_receiving_mail_index || '',
        mail_recipient: this.form.legal_receiving_mail_recipient || '',
        mail_address: this.form.legal_receiving_mail_address || '',
      };
    },
    documentReceivingErrors() {
      return {
        legal_receiving_docs_method: this.errors.legal_receiving_docs_method,
        legal_receiving_mail_index: this.errors.legal_receiving_mail_index,
        legal_receiving_mail_recipient: this.errors.legal_receiving_mail_recipient,
        legal_receiving_mail_address: this.errors.legal_receiving_mail_address,
      };
    },
    managers() {
      return this.$store.state.Managers.managersList;
    },
    authErrors() {
      return this.$store.state.Auth.errors || null;
    },
    bankFormData() {
      return {
        bank_name: this.form.bank_name || '',
        bic: this.form.bic || '',
        iban: this.form.iban || '',
        bank_address: this.form.bank_address || '',
      };
    },
    bankErrors() {
      return {
        bank_name: this.errors.bank_name || '',
        bic: this.errors.bic || '',
        iban: this.errors.iban || '',
        bank_address: this.errors.bank_address || '',
      };
    },
    contactPersonData() {
      return {
        name: this.form.legal_contact_name || '',
        phone: this.form.phone || '',
        email: this.form.email || '',
      };
    },
    contactPersonErrors() {
      return {
        name: this.errors.legal_contact_name || '',
        phone: this.errors.phone || '',
        email: this.errors.email || '',
      };
    },
    companyInfoData() {
      return {
        legal_name: this.form.legal_name || '',
        legal_address: this.form.legal_address || '',
        legal_receiving_physical_address: this.form.legal_receiving_physical_address || '',
        toggleAddress: this.form.toggleAddress || false,
      };
    },
    companyInfoErrors() {
      return {
        legal_name: this.errors.legal_name || '',
        legal_address: this.errors.legal_address || '',
        legal_receiving_physical_address: this.errors.legal_receiving_physical_address || '',
      };
    },
    automationData() {
      return {
        auto_acts: this.form.auto_acts || false,
        auto_cashback: this.form.auto_cashback || false,
        access_api: this.form.access_api || false,
        is_force_bonus: this.form.is_force_bonus !== undefined ? this.form.is_force_bonus : true,
      };
    },
    agreementData() {
      return {
        offer_agreement_accepted: !!this.form.offer_agreement_accepted,
        isAgreePolicy: !!this.form.isAgreePolicy,
      };
    },
  },
  watch: {
    isLegal(val) {
      if (!val) {
        this.$store.dispatch('Dialog/setData', { label: 'activeDialog', data: 'UserData' });
      }
    },
  },
  mounted() {
    if (this.role === 'manager' || this.role === 'admin') this.getData();

    if (this.isEditable && this.accountAutocomplete) {
      if (this.activeAccount) {
        for (let key in this.form) {
          this.form[key] = this.activeAccount[key];
        }
      }

      for (let key in this.agency_account_type) {
        if (
          this.activeAccount &&
          this.activeAccount.agency_account_type &&
          this.activeAccount.agency_account_type[key]
        ) {
          this.agency_account_type[key] = this.activeAccount.agency_account_type[key];
        }
      }
      this.form.offer_agreement_accepted = true;
      this.form.isAgreePolicy = true;
      this.isCorrectInn = true;
    }
  },
  methods: {
    handleAgencyAccountTypeUpdate(newValue) {
      // Обновляем каждое свойство объекта напрямую для реактивности Vue 2
      Object.keys(newValue).forEach((key) => {
        this.$set(this.agency_account_type, key, newValue[key]);
      });
    },
    handleDocumentReceivingUpdate(newValue) {
      this.form.legal_receiving_docs_method = newValue.method || 'not_receive';
      this.form.legal_receiving_mail_index = newValue.mail_index || '';
      this.form.legal_receiving_mail_recipient = newValue.mail_recipient || '';
      this.form.legal_receiving_mail_address = newValue.mail_address || '';
    },
    handleContactPersonUpdate(field, value) {
      if (field === 'name') {
        this.form.legal_contact_name = value;
      } else if (field === 'phone') {
        this.form.phone = value;
      } else if (field === 'email') {
        this.form.email = value;
      }
    },
    handleCompanyInfoUpdate(field, value) {
      if (field === 'legal_name') {
        this.form.legal_name = value;
      } else if (field === 'legal_address') {
        this.form.legal_address = value;
      } else if (field === 'legal_receiving_physical_address') {
        this.form.legal_receiving_physical_address = value;
      } else if (field === 'toggleAddress') {
        this.form.toggleAddress = value;
      }
    },
    handleToggleAddressChange(checked) {
      if (checked === true) {
        this.form.legal_receiving_physical_address = this.form.legal_address;
        this.inputAddressDisabled = true;
      } else {
        this.form.legal_receiving_physical_address = '';
        this.inputAddressDisabled = false;
      }
    },
    handleAutomationUpdate(field, value) {
      if (field === 'auto_acts') {
        this.form.auto_acts = value;
      } else if (field === 'auto_cashback') {
        this.form.auto_cashback = value;
      } else if (field === 'access_api') {
        this.form.access_api = value;
      } else if (field === 'is_force_bonus') {
        this.form.is_force_bonus = value;
      }
    },
    handleAgreementUpdate(field, value) {
      if (field === 'offer_agreement_accepted') {
        this.form.offer_agreement_accepted = value;
      } else if (field === 'isAgreePolicy') {
        this.form.isAgreePolicy = value;
      }
    },
    clearForm() {
      this.form.is_legal = true;
      this.form.legal_address = '';
      (this.inn = ''),
        (this.bic = ''),
        (this.iban = ''),
        (this.swift_code = ''),
        (this.bank_name = ''),
        (this.bank_address = ''),
        (this.legal_name = ''),
        (this.legal_address = ''),
        (this.legal_contact_name = ''),
        (this.legal_contact_last_name = ''),
        (this.legal_receiving_docs_method = 'not_receive'),
        (this.legal_receiving_mail_index = ''),
        (this.legal_receiving_mail_address = ''),
        (this.legal_receiving_mail_recipient = ''),
        (this.legal_receiving_physical_address = ''),
        (this.form.email = ''),
        (this.form.phone = ''),
        (this.position = ''),
        (this.based_position = ''),
        (this.form.offer_agreement_accepted = false),
        (this.form.isAgreePolicy = false);
      (this.contract_signature_firstname = ''),
        (this.contract_signature_lastname = ''),
        (this.contract_signature_position = ''),
        (this.contract_signature_document = '');
    },
    saveData() {
      if (this.isEditable && !this.accountAutocomplete) {
        this.addNewAccount();
      } else if (this.isEditable && this.accountAutocomplete) {
        this.editAccount();
      } else {
        this.addNewAccount();
      }
    },
    editAccount() {
      this.validateForm();
      if (this.isError) {
        return this.failValidation();
      }
      this.loading = true;
      const dataForm = {};
      for (let key in this.form) {
        if (this.form[key]) {
          dataForm[key] = this.form[key];
        }
      }

      const prodEnv = process.env.VUE_APP_API_URL === 'https://api.ads-profit.com/api/v1';
      dataForm.manager_id = this.form.manager_id
        ? this.form.manager_id
        : prodEnv
        ? '6cc2f4c6-2300-499c-b9f7-b7dc2720a404'
        : '373434f3-c000-4728-b179-f2a54a99887d';

      dataForm.is_legal = this.form.is_legal;
      dataForm.agency_account_type = { ...this.agency_account_type };
      dataForm.is_legal = this.form.is_legal;
      dataForm.ba_prefix = this.form.ba_prefix;
      dataForm.auto_acts = this.form.auto_acts;
      dataForm.auto_cashback = this.form.auto_cashback;
      dataForm.access_api = this.form.access_api;
      dataForm.is_force_bonus = this.form.is_force_bonus;
      dataForm.advt_tax = this.form.advt_tax;
      dataForm.course_percents = this.removeEmpty(this.form.course_percents);

      // Force-disable removed platforms even if form was prefilled from server (edit mode)
      const removedPlatforms = ['vkontakte', 'mytarget', 'vkmarket'];
      if (dataForm.tax_conditions) {
        removedPlatforms.forEach((p) => {
          dataForm.tax_conditions[p] = false;
        });
      }
      if (dataForm.visible_services) {
        removedPlatforms.forEach((p) => {
          dataForm.visible_services[p] = false;
        });
      }

      const data = {
        id: this.activeAccount.id,
        data: dataForm,
      };
      this.$store
        .dispatch('Auth/editBusinessAccount', data)
        .then(() => {
          this.loading = false;
          this.dialogVisible = false;
          this.$notify({
            message: this.$t('business_account_data.changes_saved'),
            type: 'success',
          });
          this.clearForm();
          this.closeDialog();
        })
        .catch((error) => {
          this.loading = false;
          // Извлекаем конкретное сообщение об ошибке из ответа сервера
          let errorMessage = this.$t('business_account_data.error');
          if (error && error.response && error.response.data) {
            if (error.response.data.errors) {
              // Если ошибка приходит как строка (например, "Ошибка при подключении к 1C")
              if (typeof error.response.data.errors === 'string') {
                errorMessage = error.response.data.errors;
              }
              // Если ошибка приходит как объект с ключом general
              else if (error.response.data.errors.general) {
                errorMessage = error.response.data.errors.general;
              }
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error;
            }
          }
          this.$notify({
            message: errorMessage,
            type: 'error',
          });
        });
    },
    addNewAccount() {
      this.validateForm();
      if (this.isError) {
        return this.failValidation();
      }
      this.loading = true;
      const data = {};
      for (let key in this.form) {
        if (this.form[key]) {
          data[key] = this.form[key];
        }
      }

      const prodEnv = process.env.VUE_APP_API_URL === 'https://api.ads-profit.com/api/v1';
      data.manager_id = this.form.manager_id
        ? this.form.manager_id
        : prodEnv
        ? '6cc2f4c6-2300-499c-b9f7-b7dc2720a404'
        : '373434f3-c000-4728-b179-f2a54a99887d';

      data.agency_account_type = { ...this.agency_account_type };
      data.is_legal = this.form.is_legal;
      data.ba_prefix = this.form.ba_prefix;
      data.auto_acts = this.form.auto_acts;
      data.auto_cashback = this.form.auto_cashback;
      data.access_api = this.form.access_api;
      data.is_force_bonus = this.form.is_force_bonus;
      data.advt_tax = this.form.advt_tax;
      data.course_percents = this.removeEmpty(this.form.course_percents);

      // Force-disable removed platforms even if form was prefilled from server (edit mode)
      const removedPlatforms = ['vkontakte', 'mytarget', 'vkmarket'];
      if (data.tax_conditions) {
        removedPlatforms.forEach((p) => {
          data.tax_conditions[p] = false;
        });
      }
      if (data.visible_services) {
        removedPlatforms.forEach((p) => {
          data.visible_services[p] = false;
        });
      }
      if (this.role === 'admin' || this.role === 'manager' || this.role === 'finance') {
        data.user_id = this.$store.state.Dialog.userIdForAccount;
        const token = localStorage.getItem('token');
        this.$store
          .dispatch('Auth/createBusinessAccountLikeManager', { data, token })
          .then(() => {
            this.loading = false;
            this.dialogVisible = false;
            this.$notify({
              message: this.$t('business_account_data.accaunt_created'),
              type: 'success',
            });
            this.clearForm();
            this.closeDialog();
          })
          .catch(() => {
            this.loading = false;
            this.$notify({
              message: this.$t('business_account_data.accaunt_error'),
              type: 'error',
            });
          });
      } else {
        this.$store
          .dispatch('Auth/createBusinessAccount', data)
          .then(() => {
            this.loading = false;
            this.dialogVisible = false;
            this.$notify({
              message: this.$t('business_account_data.accaunt_created'),
              type: 'success',
            });
            this.clearForm();
            this.closeDialog();
          })
          .catch(() => {
            this.loading = false;
            this.$notify({
              message: this.$t('business_account_data.error'),
              type: 'error',
            });
          });
      }
    },
    validateForm() {
      this.clearFormErrors();
      if (!this.form.email) {
        this.errors.email = this.$t('business_account_data.input_email');
      } else {
        this.validateEmail();
      }
      if (!this.form.phone) {
        this.errors.phone = this.$t('business_account_data.input_phone');
      } else {
        this.validatePhone();
      }
      if (!this.form.isAgreePolicy || !this.form.offer_agreement_accepted) {
        this.errors.isAgree = this.$t('business_account_data.should_agree');
      }
      if (
        !this.form.legal_receiving_mail_index &&
        this.form.legal_receiving_docs_method === 'by_email'
      ) {
        this.errors.legal_receiving_mail_index = this.$t('business_account_data.input_index');
      }
      if (
        !this.form.legal_receiving_mail_recipient &&
        this.form.legal_receiving_docs_method === 'by_email'
      ) {
        this.errors.legal_receiving_mail_recipient = this.$t(
          'business_account_data.input_receiver'
        );
      }
      if (
        !this.form.legal_receiving_mail_address &&
        this.form.legal_receiving_docs_method === 'by_email'
      ) {
        this.errors.legal_receiving_mail_address = this.$t('business_account_data.input_address');
      }
      if (!this.form.inn) {
        this.errors.inn = this.$t('business_account_data.input_inn');
      }
      if (!this.form.legal_name) {
        this.errors.legal_name = this.$t('business_account_data.input_legal');
      }
      if (!this.form.legal_address) {
        this.errors.legal_address = this.$t('business_account_data.input_legal_address');
      }
      if (!this.form.legal_contact_name) {
        this.errors.legal_contact_name = this.$t('business_account_data.input_name');
      }
      if (!this.form.legal_receiving_physical_address) {
        this.errors.legal_receiving_physical_address = this.$t(
          'business_account_data.input_mail_address'
        );
      }
      if (this.form.legal_receiving_docs_method === 'not_receive') {
        this.errors.legal_receiving_docs_method = this.$t(
          'business_account_data.input_receiving_way'
        );
      }
      // Валидация банковских реквизитов для RU
      if (!this.form.bank_name) {
        this.errors.bank_name = this.$t('business_account_data.input_bank');
      }
      if (!this.form.bank_address) {
        this.errors.bank_address = this.$t('business_account_data.input_bank_address');
      }
      if (!this.form.bic) {
        this.errors.bic = this.$t('business_account_data.input_bank_id');
      }
      if (!this.form.iban) {
        this.errors.iban = this.$t('business_account_data.input_correspondent_account');
      }
    },

    copyAddress() {
      if (this.form.toggleAddress === true) {
        this.form.legal_receiving_physical_address = this.form.legal_address;
        this.inputAddressDisabled = true;
      } else {
        this.form.legal_receiving_physical_address = '';
        this.inputAddressDisabled = false;
      }
    },
    closeDialog() {
      this.$store.dispatch('Dialog/closeDialog');
      this.$store.commit('Dialog/setData', { label: 'activeDialog', data: '' });
    },
    handleCheckInn(inn) {
      this.loading = true;
      const data = {
        inn: inn,
      };
      this.$store
        .dispatch('Ad/checkInn', data)
        .then((data) => {
          this.isCorrectInn = true;
          for (const key in data.data) {
            this.form[key] = data.data[key];
          }
          if (data.data.legal_address) this.isDisabledLegalAddress = true;
          this.loading = false;
        })
        .catch((err) => {
          this.isCorrectInn = false;
          this.errors.inn = err.errors.inn ? err.errors.inn[0] : err.errors;
          this.loading = false;
          this.scrollToFirstError();
        });
    },
    isObjectEmpty(obj) {
      for (let key in obj) {
        if (typeof obj[key] === 'object') {
          if (!this.isObjectEmpty(obj[key])) {
            return false;
          }
        } else if (obj[key]) {
          return false;
        }
      }
      return true;
    },
    removeEmpty(obj) {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k, v != null && v !== '' ? v : 0])
      );
    },
    handleBankDataUpdate(bankData) {
      this.form.bank_name = bankData.bank_name || '';
      this.form.bic = bankData.bic || '';
      this.form.iban = bankData.iban || '';
      this.form.bank_address = bankData.bank_address || '';
    },
    getNewData(id) {
      this.form.manager_id = id;
    },
    async getData() {
      this.loading = true;
      const token = localStorage.getItem('token');
      await this.$store.dispatch('Managers/getAllManagersList', { token });
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.dialog-add-account {
  .course-percent {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    .dialog-add-account__form-item {
      width: calc(50% - 1rem);
      height: unset;
    }
  }

  &__form-type--radio {
    margin-bottom: 1.5rem;
  }
  .button {
    display: inline-block;
  }
  // .form-input.course-markup {
  //   input + .form-input__label {
  //     font-size: 1.2rem;
  //     line-height: 1.4rem;
  //     top: 0.8rem;
  //   }
  // }
  &__form-type {
    label:first-of-type {
      border-right: 1px solid #bfc2c6;
      &.error {
        border-color: #eb5757;
      }
    }
  }

  &__form-agree-container {
    margin-bottom: 6px;
  }
}
</style>
